// src/constants/queryKeys.ts (Updated version)

// Centralized query key factory for type safety and organization
export const queryKeys = {
  categories: {
    all: ["categories"] as const,
    tree: () => [...queryKeys.categories.all, "tree"] as const,
    list: () => [...queryKeys.categories.all, "list"] as const,
    current: (categoryId?: number) =>
      [...queryKeys.categories.all, "current", categoryId ?? "all"] as const,
    currentRatios: (categoryId?: number) =>
      [
        ...queryKeys.categories.all,
        "currentRatios",
        categoryId ?? "all",
      ] as const,
    currentChanges: (categoryId?: number) =>
      [
        ...queryKeys.categories.all,
        "currentChanges",
        categoryId ?? "all",
      ] as const,
    currentRollups: (categoryId?: number) =>
      [
        ...queryKeys.categories.all,
        "currentRollups",
        categoryId ?? "all",
      ] as const,
    performance: (categoryId: number) =>
      [...queryKeys.categories.all, "performance", categoryId] as const,
    historyCompact: (
      categoryId: number,
      metricKeys: string[],
      start?: string,
      end?: string
    ) =>
      [
        ...queryKeys.categories.all,
        "historyCompact",
        categoryId,
        { metricKeys: metricKeys.slice().sort(), start, end },
      ] as const,
    totalMarketCapHistory: () =>
      [...queryKeys.categories.all, "totalMarketCapHistory"] as const,
  },
  dashboard: {
    all: ["dashboard"] as const,
    onChain: () => [...queryKeys.dashboard.all, "on-chain"] as const,
    sector: () => [...queryKeys.dashboard.all, "sector"] as const,
  },
  tokenPage: {
    all: ["tokenPage"] as const,

    financialValue: {
      all: (tokenId: string) =>
        [...queryKeys.tokenPage.all, "financialValue", tokenId] as const,
      tokenInfo: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.financialValue.all(tokenId),
          "tokenInfo",
        ] as const,
      aiReports: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.financialValue.all(tokenId),
          "aiReports",
        ] as const,
      keyMetrics: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.financialValue.all(tokenId),
          "keyMetrics",
        ] as const,
      keyRatios: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.financialValue.all(tokenId),
          "keyRatios",
        ] as const,
      customConfirmations: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.financialValue.all(tokenId),
          "customConfirmations",
        ] as const,
      historicalReturns: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.financialValue.all(tokenId),
          "historicalReturns",
        ] as const,
    },

    socialValue: {
      all: (tokenId: string) =>
        [...queryKeys.tokenPage.all, "socialValue", tokenId] as const,
      sentimentAnalytics: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.socialValue.all(tokenId),
          "sentimentAnalytics",
        ] as const,
      sentimentComments: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.socialValue.all(tokenId),
          "sentimentComments",
        ] as const,
      topVoices: (tokenId: string) =>
        [...queryKeys.tokenPage.socialValue.all(tokenId), "topVoices"] as const,
      // Future: Smart feed when ready
      smartFeed: (tokenId: string) =>
        [...queryKeys.tokenPage.socialValue.all(tokenId), "smartFeed"] as const,
    },

    utilityValue: {
      all: (tokenId: string) =>
        [...queryKeys.tokenPage.all, "utilityValue", tokenId] as const,
      basicValues: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.utilityValue.all(tokenId),
          "basicValues",
        ] as const,

      allocation: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.utilityValue.all(tokenId),
          "allocation",
        ] as const,

      unlockProgress: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.utilityValue.all(tokenId),
          "unlockProgress",
        ] as const,

      unlockSchedule: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.utilityValue.all(tokenId),
          "unlockSchedule",
        ] as const,

      burned: (tokenId: string) =>
        [...queryKeys.tokenPage.utilityValue.all(tokenId), "burned"] as const,

      largeHoldersNetflowInflowOutflow: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.utilityValue.all(tokenId),
          "largeHoldersNetflowInflowOutflow",
        ] as const,

      historicalConcentration: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.utilityValue.all(tokenId),
          "historicalConcentration",
        ] as const,

      averageTimeTokenIsHeld: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.utilityValue.all(tokenId),
          "averageTimeTokenIsHeld",
        ] as const,
      fundingRounds: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.utilityValue.all(tokenId),
          "fundingRounds",
        ] as const,
      publicTokenSales: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.utilityValue.all(tokenId),
          "publicTokenSales",
        ] as const,
      investorsAndBackers: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.utilityValue.all(tokenId),
          "investorsAndBackers",
        ] as const,
      team: (tokenId: string) =>
        [...queryKeys.tokenPage.utilityValue.all(tokenId), "team"] as const,
    },

    onChain: {
      all: (tokenId: string) =>
        [...queryKeys.tokenPage.all, "onChain", tokenId] as const,
      stakingMetrics: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.onChain.all(tokenId),
          "stakingMetrics",
        ] as const,
      institutionalFlows: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.onChain.all(tokenId),
          "institutionalFlows",
        ] as const,
      circulatingSupply: (tokenId: string) =>
        [
          ...queryKeys.tokenPage.onChain.all(tokenId),
          "circulatingSupply",
        ] as const,
    },
  },

  // Media Insights
  mediaInsights: {
    all: ["mediaInsights"] as const,
    allChannels: () => [...queryKeys.mediaInsights.all, "allChannels"] as const,
    // YouTube V2 API endpoints
    youtube: {
      all: () => [...queryKeys.mediaInsights.all, "youtube"] as const,
      allVideos: () =>
        [...queryKeys.mediaInsights.youtube.all(), "allVideos"] as const,
      videoDetail: (
        videoId: string,
        includeSections = true,
        includeTickers = true
      ) =>
        [
          ...queryKeys.mediaInsights.youtube.all(),
          "videoDetail",
          videoId,
          includeSections,
          includeTickers,
        ] as const,
      channelVideos: (channelId: string, limit = 50, offset = 0) =>
        [
          ...queryKeys.mediaInsights.youtube.all(),
          "channelVideos",
          channelId,
          limit,
          offset,
        ] as const,
    },
    tweets: (params?: {
      author_usernames?: string[];
      tickers?: string[];
      start_datetime?: string;
      end_datetime?: string;
      limit?: number;
      offset?: number;
      min_alpha_grade?: number;
      max_alpha_grade?: number;
    }) =>
      [
        ...queryKeys.mediaInsights.all,
        "tweets",
        {
          author_usernames: params?.author_usernames?.slice().sort() || [],
          tickers: params?.tickers?.slice().sort() || [],
          start_datetime: params?.start_datetime,
          end_datetime: params?.end_datetime,
          limit: params?.limit,
          offset: params?.offset,
          min_alpha_grade: params?.min_alpha_grade,
          max_alpha_grade: params?.max_alpha_grade,
        },
      ] as const,
    twitterFilters: () =>
      [...queryKeys.mediaInsights.all, "twitterFilters"] as const,
    news: (params?: {
      limit?: number;
      offset?: number;
      min_alpha_grade?: number;
      max_alpha_grade?: number;
      start_datetime?: string;
      end_datetime?: string;
    }) =>
      [
        ...queryKeys.mediaInsights.all,
        "news",
        {
          limit: params?.limit,
          offset: params?.offset,
          min_alpha_grade: params?.min_alpha_grade,
          max_alpha_grade: params?.max_alpha_grade,
          start_datetime: params?.start_datetime,
          end_datetime: params?.end_datetime,
        },
      ] as const,
  },

  // Coinglass API queries
  coinglass: {
    all: ["coinglass"] as const,

    fundingRate: {
      all: () => [...queryKeys.coinglass.all, "fundingRate"] as const,
      history: (params: { ticker: string; exchanges: string[] }) =>
        [
          ...queryKeys.coinglass.fundingRate.all(),
          "history",
          {
            ticker: params.ticker,
            exchanges: params.exchanges.slice().sort(), // Sort for consistency
          },
        ] as const,
    },

    tokenPrice: {
      all: () => [...queryKeys.coinglass.all, "tokenPrice"] as const,
      history: (params: { ticker: string }) =>
        [
          ...queryKeys.coinglass.tokenPrice.all(),
          "history",
          params.ticker,
        ] as const,
    },

    openInterest: {
      all: () => [...queryKeys.coinglass.all, "openInterest"] as const,
      history: (params: { ticker: string }) =>
        [
          ...queryKeys.coinglass.openInterest.all(),
          "history",
          params.ticker,
        ] as const,
    },

    weightedFundingRate: {
      all: () => [...queryKeys.coinglass.all, "weightedFundingRate"] as const,
      history: (params: { ticker: string; dataType: "oiw" | "volw" }) =>
        [
          ...queryKeys.coinglass.weightedFundingRate.all(),
          "history",
          params.ticker,
          params.dataType,
        ] as const,
    },

    etfInflows: {
      all: () => [...queryKeys.coinglass.all, "etfInflows"] as const,
      history: (params: { ticker: string }) =>
        [
          ...queryKeys.coinglass.etfInflows.all(),
          "history",
          params.ticker,
        ] as const,
      chart: (params: { ticker: string }) =>
        [
          ...queryKeys.coinglass.etfInflows.all(),
          "chart",
          params.ticker,
        ] as const,
    },

    fearGreed: {
      all: () => [...queryKeys.coinglass.all, "fearGreed"] as const,
      chart: (params?: {
        token_id?: number | undefined;
        start_date?: string | undefined;
        end_date?: string | undefined;
        limit?: number | undefined;
      }) =>
        [
          ...queryKeys.coinglass.fearGreed.all(),
          "chart",
          {
            token_id: params?.token_id,
            start_date: params?.start_date,
            end_date: params?.end_date,
            limit: params?.limit,
          },
        ] as const,
    },

    holderSupply: {
      all: () => [...queryKeys.coinglass.all, "holderSupply"] as const,
      chart: (params?: {
        token_id?: number | undefined;
        start_date?: string | undefined;
        end_date?: string | undefined;
        limit?: number | undefined;
      }) =>
        [
          ...queryKeys.coinglass.holderSupply.all(),
          "chart",
          {
            token_id: params?.token_id,
            start_date: params?.start_date,
            end_date: params?.end_date,
            limit: params?.limit,
          },
        ] as const,
    },

    holderRealizedPrice: {
      all: () => [...queryKeys.coinglass.all, "holderRealizedPrice"] as const,
      chart: (params?: {
        token_id?: number | undefined;
        start_date?: string | undefined;
        end_date?: string | undefined;
        limit?: number | undefined;
      }) =>
        [
          ...queryKeys.coinglass.holderRealizedPrice.all(),
          "chart",
          {
            token_id: params?.token_id,
            start_date: params?.start_date,
            end_date: params?.end_date,
            limit: params?.limit,
          },
        ] as const,
    },

    globalM2: {
      all: () => [...queryKeys.coinglass.all, "globalM2"] as const,
      chart: (params?: {
        token_id?: number | undefined;
        start_date?: string | undefined;
        end_date?: string | undefined;
        limit?: number | undefined;
      }) =>
        [
          ...queryKeys.coinglass.globalM2.all(),
          "chart",
          {
            token_id: params?.token_id,
            start_date: params?.start_date,
            end_date: params?.end_date,
            limit: params?.limit,
          },
        ] as const,
    },

    bullMarketPeakIndicators: {
      all: () =>
        [...queryKeys.coinglass.all, "bullMarketPeakIndicators"] as const,
      list: (params?: {
        latest_only?: boolean | undefined;
        start_date?: string | undefined;
        end_date?: string | undefined;
        limit?: number | undefined;
      }) =>
        [
          ...queryKeys.coinglass.bullMarketPeakIndicators.all(),
          "list",
          {
            latest_only: params?.latest_only,
            start_date: params?.start_date,
            end_date: params?.end_date,
            limit: params?.limit,
          },
        ] as const,
    },

    ahr999Chart: {
      all: () => [...queryKeys.coinglass.all, "ahr999Chart"] as const,
      data: (params?: {
        start_date?: string | undefined;
        end_date?: string | undefined;
        limit?: number | undefined;
      }) =>
        [
          ...queryKeys.coinglass.ahr999Chart.all(),
          "data",
          {
            start_date: params?.start_date,
            end_date: params?.end_date,
            limit: params?.limit,
          },
        ] as const,
    },

    puellMultiple: {
      all: () => [...queryKeys.coinglass.all, "puellMultiple"] as const,
      chart: (params?: {
        start_timestamp?: number | undefined;
        end_timestamp?: number | undefined;
        limit?: number | undefined;
      }) =>
        [
          ...queryKeys.coinglass.puellMultiple.all(),
          "chart",
          {
            start_timestamp: params?.start_timestamp,
            end_timestamp: params?.end_timestamp,
            limit: params?.limit,
          },
        ] as const,
    },

    stockToFlow: {
      all: () => [...queryKeys.coinglass.all, "stockToFlow"] as const,
      chart: (params?: {
        start_timestamp?: number | undefined;
        end_timestamp?: number | undefined;
        limit?: number | undefined;
      }) =>
        [
          ...queryKeys.coinglass.stockToFlow.all(),
          "chart",
          {
            start_timestamp: params?.start_timestamp,
            end_timestamp: params?.end_timestamp,
            limit: params?.limit,
          },
        ] as const,
    },

    piCycleTop: {
      all: () => [...queryKeys.coinglass.all, "piCycleTop"] as const,
      chart: (params?: {
        start_timestamp?: number | undefined;
        end_timestamp?: number | undefined;
        limit?: number | undefined;
      }) =>
        [
          ...queryKeys.coinglass.piCycleTop.all(),
          "chart",
          {
            start_timestamp: params?.start_timestamp,
            end_timestamp: params?.end_timestamp,
            limit: params?.limit,
          },
        ] as const,
    },

    goldenRatioMultiplier: {
      all: () => [...queryKeys.coinglass.all, "goldenRatioMultiplier"] as const,
      chart: (params?: {
        start_timestamp?: number | undefined;
        end_timestamp?: number | undefined;
        limit?: number | undefined;
      }) =>
        [
          ...queryKeys.coinglass.goldenRatioMultiplier.all(),
          "chart",
          {
            start_timestamp: params?.start_timestamp,
            end_timestamp: params?.end_timestamp,
            limit: params?.limit,
          },
        ] as const,
    },
    // 1. 200-Week Moving Average Heatmap
    movingAverage200WHeatmap: {
      all: () => [...queryKeys.coinglass.all, "movingAverage200WHeatmap"] as const,
      chart: (params?: { limit?: number | undefined; start_timestamp?: number | undefined; end_timestamp?: number | undefined }) =>
        [
          ...queryKeys.coinglass.movingAverage200WHeatmap.all(),
          "chart",
          { limit: params?.limit, start_timestamp: params?.start_timestamp, end_timestamp: params?.end_timestamp },
        ] as const,
    },

    // 2. Altcoin Season Index
    altcoinSeasonIndex: {
      all: () => [...queryKeys.coinglass.all, "altcoinSeasonIndex"] as const,
      chart: (params?: { limit?: number | undefined; start_timestamp?: number | undefined; end_timestamp?: number | undefined; include_marketcap?: boolean | undefined }) =>
        [
          ...queryKeys.coinglass.altcoinSeasonIndex.all(),
          "chart",
          { limit: params?.limit, start_timestamp: params?.start_timestamp, end_timestamp: params?.end_timestamp, include_marketcap: params?.include_marketcap },
        ] as const,
    },

    // 3. Bitcoin Bubble Index
    bitcoinBubbleIndex: {
      all: () => [...queryKeys.coinglass.all, "bitcoinBubbleIndex"] as const,
      chart: (params?: { limit?: number | undefined; start_date?: string | undefined; end_date?: string | undefined }) =>
        [
          ...queryKeys.coinglass.bitcoinBubbleIndex.all(),
          "chart",
          { limit: params?.limit, start_date: params?.start_date, end_date: params?.end_date },
        ] as const,
    },

    // 4. Bitcoin Macro Oscillator
    bitcoinMacroOscillator: {
      all: () => [...queryKeys.coinglass.all, "bitcoinMacroOscillator"] as const,
      chart: (params?: { limit?: number | undefined; start_timestamp?: number | undefined; end_timestamp?: number | undefined }) =>
        [
          ...queryKeys.coinglass.bitcoinMacroOscillator.all(),
          "chart",
          { limit: params?.limit, start_timestamp: params?.start_timestamp, end_timestamp: params?.end_timestamp },
        ] as const,
    },

    // 5. Bitcoin Rainbow Chart
    bitcoinRainbowChart: {
      all: () => [...queryKeys.coinglass.all, "bitcoinRainbowChart"] as const,
      chart: (params?: { limit?: number | undefined; start_timestamp?: number | undefined; end_timestamp?: number | undefined }) =>
        [
          ...queryKeys.coinglass.bitcoinRainbowChart.all(),
          "chart",
          { limit: params?.limit, start_timestamp: params?.start_timestamp, end_timestamp: params?.end_timestamp },
        ] as const,
    },

    // 6. Bitcoin Reserve Risk
    bitcoinReserveRisk: {
      all: () => [...queryKeys.coinglass.all, "bitcoinReserveRisk"] as const,
      chart: (params?: { limit?: number | undefined; start_timestamp?: number | undefined; end_timestamp?: number | undefined }) =>
        [
          ...queryKeys.coinglass.bitcoinReserveRisk.all(),
          "chart",
          { limit: params?.limit, start_timestamp: params?.start_timestamp, end_timestamp: params?.end_timestamp },
        ] as const,
    },

    // 7. Bitcoin RHODL Ratio
    bitcoinRHODLRatio: {
      all: () => [...queryKeys.coinglass.all, "bitcoinRHODLRatio"] as const,
      chart: (params?: { limit?: number | undefined; start_timestamp?: number | undefined; end_timestamp?: number | undefined }) =>
        [
          ...queryKeys.coinglass.bitcoinRHODLRatio.all(),
          "chart",
          { limit: params?.limit, start_timestamp: params?.start_timestamp, end_timestamp: params?.end_timestamp },
        ] as const,
    },
  },
} as const;

// Addition to src/constants/queryKeys.ts
// Add this section to the existing queryKeys object

export const chartQueryKeys = {
  all: ["charts"] as const,

  timeSeries: (params: {
    metricId: string;
    sector: string;
    chartType: string;
    tokenIds?: string[];
  }) =>
    [
      ...chartQueryKeys.all,
      "timeSeries",
      {
        metricId: params.metricId,
        sector: params.sector,
        chartType: params.chartType,
        tokenIds: params.tokenIds?.sort() || [],
      },
    ] as const,

  // New batched history queries
  history: (params: {
    categoryId: number;
    metricKeys?: string[];
    ratioKeys?: string[];
    kind: "metrics" | "ratios";
  }) =>
    [
      ...chartQueryKeys.all,
      "history",
      params.kind,
      {
        categoryId: params.categoryId,
        metricKeys: params.metricKeys?.slice().sort() || [],
        ratioKeys: params.ratioKeys?.slice().sort() || [],
      },
    ] as const,
};

export const searchQueryKeys = {
  all: ["search"] as const,
  tokens: (query: string) =>
    [
      ...searchQueryKeys.all,
      "tokens",
      { q: query.trim().toLowerCase(), limit: 10 },
    ] as const,
};

export const macroQueryKeys = {
  all: ["macro"] as const,

  // API endpoints
  overview: () => [...macroQueryKeys.all, "overview"] as const,

  economy: () => [...macroQueryKeys.all, "economy"] as const,

  correlations: () => [...macroQueryKeys.all, "correlations"] as const,

  calendar: (
    params: {
      startDate?: string;
      endDate?: string;
    } = {}
  ) =>
    [
      ...macroQueryKeys.all,
      "calendar",
      {
        startDate: params.startDate,
        endDate: params.endDate,
      },
    ] as const,

  fedRateProbabilities: () =>
    [...macroQueryKeys.all, "fedRateProbabilities"] as const,

  fxCommodities: () => [...macroQueryKeys.all, "fxCommodities"] as const,
};

// FRED Macro Charts Query Keys
export const fredQueryKeys = {
  all: ["fred"] as const,

  // Chart data (ECharts-ready)
  chart: (params: {
    metricId: string;
    real?: boolean;
    startDate?: string;
    endDate?: string;
    limit?: number;
  }) =>
    [
      ...fredQueryKeys.all,
      "chart",
      params.metricId,
      {
        real: params.real ?? false,
        startDate: params.startDate,
        endDate: params.endDate,
        limit: params.limit,
      },
    ] as const,

  // Metric metadata (feature flags, display config)
  metadata: (metricId: string) =>
    [...fredQueryKeys.all, "metadata", metricId] as const,

  // All metrics list
  metricsList: (params?: { category?: string; activeOnly?: boolean }) =>
    [
      ...fredQueryKeys.all,
      "metricsList",
      {
        category: params?.category,
        activeOnly: params?.activeOnly ?? true,
      },
    ] as const,

  // Recession periods
  recessions: (params?: { startDate?: string; endDate?: string }) =>
    [
      ...fredQueryKeys.all,
      "recessions",
      {
        startDate: params?.startDate,
        endDate: params?.endDate,
      },
    ] as const,

  // Raw series data (for custom processing)
  series: (params: {
    seriesId: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    order?: "asc" | "desc";
  }) =>
    [
      ...fredQueryKeys.all,
      "series",
      params.seriesId,
      {
        startDate: params.startDate,
        endDate: params.endDate,
        limit: params.limit,
        order: params.order ?? "asc",
      },
    ] as const,
};

export const portfolioQueryKeys = {
  all: ["portfolio"] as const,

  transactions: {
    all: () => [...portfolioQueryKeys.all, "transactions"] as const,
    list: () => [...portfolioQueryKeys.transactions.all(), "list"] as const,
  },

  // (Removed) legacy historical performance keys
  nav: {
    all: () => [...portfolioQueryKeys.all, "nav"] as const,
    data: () => [...portfolioQueryKeys.nav.all(), "data"] as const,
  },
  performanceVsBenchmark: {
    all: () => [...portfolioQueryKeys.all, "performance-vs-benchmark"] as const,
    data: () =>
      [...portfolioQueryKeys.performanceVsBenchmark.all(), "data"] as const,
  },
  holdings: {
    all: () => [...portfolioQueryKeys.all, "holdings"] as const,
    list: () => [...portfolioQueryKeys.holdings.all(), "list"] as const,
  },
  realized: {
    all: () => [...portfolioQueryKeys.all, "realized-pnl"] as const,
    list: () => [...portfolioQueryKeys.realized.all(), "list"] as const,
  },
};

// Assistant
export const assistantQueryKeys = {
  all: ["assistant"] as const,
  conversations: {
    all: () => [...assistantQueryKeys.all, "conversations"] as const,
    list: (limit: number = 50) =>
      [...assistantQueryKeys.conversations.all(), "list", { limit }] as const,
  },
  messages: (conversationId: string, limit: number = 50) =>
    [...assistantQueryKeys.all, "messages", { conversationId, limit }] as const,
  usage: () => [...assistantQueryKeys.all, "usage"] as const,
} as const;

export const riskQueryKeys = {
  all: ["risk"] as const,
  // New policy-based keys
  portfolioCurrent: () => [...riskQueryKeys.all, "portfolioCurrent"] as const,
  portfolioHistoryMetric: (metricKey: string) =>
    [...riskQueryKeys.all, "portfolioHistoryMetric", metricKey] as const,
  policies: () => [...riskQueryKeys.all, "policies"] as const,
  policy: (policyId: string) =>
    [...riskQueryKeys.policies(), policyId] as const,
  events: (params?: { limit?: number }) =>
    params && typeof params.limit === "number"
      ? ([...riskQueryKeys.all, "events", { limit: params.limit }] as const)
      : ([...riskQueryKeys.all, "events"] as const),
};

// Investors/Funds Query Keys
export const investorsQueryKeys = {
  all: ["investors"] as const,
  funds: () => [...investorsQueryKeys.all, "funds"] as const,
  detail: (investorId: string) =>
    [...investorsQueryKeys.all, "detail", investorId] as const,
};

// User Profile Query Keys
export const userProfileQueryKeys = {
  all: ["userProfile"] as const,
  profile: () => [...userProfileQueryKeys.all, "profile"] as const,
  preferences: () => [...userProfileQueryKeys.all, "preferences"] as const,
};

// Signal Snapshots Query Keys
export const signalQueryKeys = {
  all: ["signals"] as const,

  series: (params: {
    runId: string;
    signalName: string;
    rawFeature?: string;
    startTs?: string;
    endTs?: string;
  }) =>
    [
      ...signalQueryKeys.all,
      "series",
      {
        runId: params.runId,
        signalName: params.signalName,
        rawFeature: params.rawFeature,
        startTs: params.startTs,
        endTs: params.endTs,
      },
    ] as const,
};

// Admin Dashboard Query Keys
export const adminQueryKeys = {
  all: ["admin"] as const,
  accessRequests: {
    all: () => [...adminQueryKeys.all, "accessRequests"] as const,
    list: (params?: { status?: string; skip?: number; limit?: number }) =>
      [
        ...adminQueryKeys.accessRequests.all(),
        "list",
        {
          status: params?.status,
          skip: params?.skip,
          limit: params?.limit,
        },
      ] as const,
  },
  activeUsers: {
    all: () => [...adminQueryKeys.all, "activeUsers"] as const,
    list: (params?: { skip?: number; limit?: number; search?: string }) =>
      [
        ...adminQueryKeys.activeUsers.all(),
        "list",
        { skip: params?.skip, limit: params?.limit, search: params?.search },
      ] as const,
  },
};
